import { Lock, MoveDiagonal, MoveHorizontal, MoveVertical, X } from 'lucide-react';
import { type KeyboardEvent, type RefObject, useId, useState } from 'react';
import type { DragAbilities, ScheduleView } from '../timeline/dropLogic.js';
import { LegendContent } from './LegendContent.js';
import { type DragPhrase, moveRules, stepSentence, viewerLine } from './moveRules.js';

/**
 * 329 (US1, FR-005..FR-009, research R5) — the schedule Guide: what each bar can do, what a drop does,
 * and what the colours mean, in a panel over the right of the grid.
 *
 * 🔴 Non-modal (`aria-modal="false"`) on purpose: the grid stays usable, so a drag works while the
 * Guide is open. Esc closes it only when focus is inside it, so an open confirm box keeps its own Esc.
 * It is a sibling of the timeline and takes no bar's props, so opening it re-renders no bar (318).
 */

const DRAG_ICON: Record<DragPhrase, typeof Lock> = {
  diagonal: MoveDiagonal,
  'car-only': MoveVertical,
  'time-only': MoveHorizontal,
  none: Lock,
};

type Tab = 'move' | 'colours';

export interface ScheduleGuideProps {
  abilities: DragAbilities;
  view: ScheduleView;
  onClose: () => void;
  /** Where focus goes when the Guide closes: the toolbar's Guide button. */
  returnFocusTo?: RefObject<HTMLElement | null>;
  className?: string;
}

const HEADING = 'text-[11px] font-semibold uppercase tracking-[1px] text-steel';
const LIST =
  'm-0 flex list-disc flex-col gap-1.5 pl-[18px] text-[13px] leading-normal text-charcoal';

export function ScheduleGuide({
  abilities,
  view,
  onClose,
  returnFocusTo,
  className = 'absolute inset-y-0 right-0 z-30 w-full sm:w-[420px]',
}: ScheduleGuideProps) {
  const [tab, setTab] = useState<Tab>('move');
  const id = useId();
  const titleId = `${id}-title`;
  const rules = moveRules();

  const close = () => {
    onClose();
    returnFocusTo?.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
  };

  const tabClass = (active: boolean) =>
    [
      'border-0 border-b-2 bg-transparent px-1 py-2.5 text-sm font-semibold',
      active ? 'border-brand-blue text-brand-blue' : 'border-transparent text-steel hover:text-ink',
    ].join(' ');

  // A side panel, not `<dialog open>`: the element's UA styles centre it, and it must sit at the grid's
  // right edge and stay out of the way of a drag.
  return (
    // biome-ignore lint/a11y/useSemanticElements: non-modal side panel, see above
    <aside
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      data-testid="schedule-guide"
      onKeyDown={onKeyDown}
      className={[
        className,
        'flex flex-col border-l border-hairline bg-canvas text-ink shadow-xl',
      ].join(' ')}
    >
      <div className="flex items-center justify-between pt-4 pr-4 pb-3 pl-5">
        <h2 id={titleId} className="m-0 text-lg font-semibold">
          Schedule guide
        </h2>
        <button
          type="button"
          aria-label="Close the guide"
          data-testid="schedule-guide-close"
          onClick={close}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-hairline-strong text-slate hover:bg-surface-soft"
        >
          <X aria-hidden="true" size={14} />
        </button>
      </div>

      <div
        role="tablist"
        aria-label="Guide sections"
        className="flex gap-5 border-b border-hairline px-5"
      >
        <button
          type="button"
          role="tab"
          id={`${id}-tab-move`}
          aria-selected={tab === 'move'}
          aria-controls={`${id}-panel`}
          data-testid="schedule-guide-tab-move"
          onClick={() => setTab('move')}
          className={tabClass(tab === 'move')}
        >
          How to move things
        </button>
        <button
          type="button"
          role="tab"
          id={`${id}-tab-colours`}
          aria-selected={tab === 'colours'}
          aria-controls={`${id}-panel`}
          data-testid="schedule-guide-tab-colours"
          onClick={() => setTab('colours')}
          className={tabClass(tab === 'colours')}
        >
          What the colours mean
        </button>
      </div>

      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-tab-${tab}`}
        className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-6"
      >
        {tab === 'move' ? (
          <div className="flex flex-col gap-5">
            <p
              data-testid="schedule-guide-viewer"
              className="m-0 rounded-lg bg-surface-feature px-3 py-2.5 text-sm font-medium"
            >
              {viewerLine(abilities)}
            </p>

            <section className="flex flex-col gap-2">
              <h3 className={HEADING}>What each bar can do</h3>
              <table
                data-testid="schedule-guide-rules"
                className="w-full table-fixed border-collapse text-[13px]"
              >
                <colgroup>
                  <col className="w-[46%]" />
                  <col />
                  <col className="w-14" />
                </colgroup>
                <thead>
                  <tr className="text-left text-steel">
                    <th scope="col" className="border-b border-hairline py-1.5 pr-2 font-medium">
                      Bar
                    </th>
                    <th scope="col" className="border-b border-hairline px-2 py-1.5 font-medium">
                      Drag it to
                    </th>
                    <th
                      scope="col"
                      className="border-b border-hairline py-1.5 pl-2 text-center font-medium"
                    >
                      Resize
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => {
                    const Icon = DRAG_ICON[rule.drag];
                    return (
                      <tr key={rule.kind} data-kind={rule.kind} data-drag={rule.drag}>
                        <td className="border-b border-hairline-soft py-2 pr-2 align-top">
                          <div className="flex items-center gap-2">
                            <span
                              aria-hidden="true"
                              className={[
                                'inline-block h-3 w-5 shrink-0 rounded-sm',
                                rule.swatch,
                              ].join(' ')}
                            />
                            <span className="font-medium">{rule.label}</span>
                          </div>
                          {rule.note && (
                            <div className="mt-0.5 ml-7 text-xs text-steel">{rule.note}</div>
                          )}
                        </td>
                        <td className="border-b border-hairline-soft px-2 py-2 align-top">
                          <span
                            className={[
                              'flex items-start gap-1.5',
                              rule.drag === 'none' ? 'text-steel' : 'text-ink',
                            ].join(' ')}
                          >
                            <Icon aria-hidden="true" size={15} className="mt-0.5 shrink-0" />
                            {rule.phrase}
                          </span>
                        </td>
                        <td className="border-b border-hairline-soft py-2 pl-2 text-center align-top text-slate">
                          {rule.resize ? (rule.kind === 'block' ? 'Yes' : 'Yes*') : 'No'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="m-0 text-xs text-steel">
                * Resize by dragging a bar’s edge: single, unpaid bookings only, in Day and Week.
              </p>
            </section>

            <section className="flex flex-col gap-2">
              <h3 className={HEADING}>When you drop</h3>
              <ul data-testid="schedule-guide-drop" className={LIST}>
                <li>
                  Nothing is saved until you press{' '}
                  <strong className="font-semibold">Confirm</strong>. Esc cancels.
                </li>
                <li>
                  Red with an outline means a clash. The outline is the nearest free time; choose{' '}
                  <strong className="font-semibold">Use …</strong> to take it.
                </li>
                <li>A paid booking keeps its price. Changing its car asks for a reason.</li>
                <li>An unpaid booking is repriced when its car changes.</li>
                <li>A move never changes a booking’s status.</li>
                <li>
                  The customer is told by email and in the app, except when a finished rental’s
                  record is corrected.
                </li>
                <li>{stepSentence()}</li>
              </ul>
            </section>

            <section className="flex flex-col gap-2">
              <h3 className={HEADING}>Other actions</h3>
              <ul data-testid="schedule-guide-actions" className={LIST}>
                <li>Click a bar to see its details.</li>
                <li>Drag a bar’s edge to change its start or end, where the table allows it.</li>
                <li>Drag across an empty part of a row in Day view to add a block.</li>
                <li>
                  Keyboard: Tab to a bar, Space to pick it up, arrows to move, Enter to drop, Esc to
                  cancel.
                </li>
              </ul>
            </section>

            <section className="flex flex-col gap-2">
              <h3 className={HEADING}>Who can do what</h3>
              <dl
                data-testid="schedule-guide-who"
                className="m-0 grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]"
              >
                <dt className="font-semibold">Admin</dt>
                <dd className="m-0 text-charcoal">Moves bookings and blocks, and adds blocks.</dd>
                <dt className="font-semibold">Supplier</dt>
                <dd className="m-0 text-charcoal">
                  Moves and adds blocks on their own cars. Reschedules a booking from its page.
                </dd>
                <dt className="font-semibold">Host</dt>
                <dd className="m-0 text-charcoal">
                  Sees the schedule. Reschedules a booking from its page.
                </dd>
              </dl>
            </section>
          </div>
        ) : (
          <div data-testid="schedule-guide-legend">
            <LegendContent view={view} />
          </div>
        )}
      </div>
    </aside>
  );
}
